import { ButtonBase, Stack, styled, SxProps, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import React from "react";
import { useTranslation } from "react-i18next";

import { getPermissionReadableName, PermissionType } from "../../../Core/Api/Types/permission-type";
import PermissionIcon from "./permission-icon";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";

interface PermissionCardProps {
  permission: PermissionType;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  sx: SxProps;
}

const PermissionCard = (props: PermissionCardProps) => {
  const { t } = useTranslation();
  const { permission, selected, disabled, onClick } = props;

  const { name, description } = React.useMemo(() => {
    const name = getPermissionReadableName(permission);
    let description = "";

    const context = selected ? "can" : "cannot";

    switch (permission) {
      case PermissionType.Access:
        description = t("page:permissionCard.description.playItem", { context });
        break;
      case PermissionType.Comment:
        description = t("page:permissionCard.description.comment", { context });
        break;
      case PermissionType.CreateObject:
        description = t("page:permissionCard.description.uploadFiles", { context });
        break;
      case PermissionType.Download:
        description = t("page:permissionCard.description.downloadFiles", { context });
        break;
      case PermissionType.MetadataEdit:
        description = t("page:permissionCard.description.editMetadata", { context });
        break;
      case PermissionType.Share:
        description = t("page:permissionCard.description.manageAccess", { context });
        break;
      case PermissionType.Trash:
        description = t("page:permissionCard.description.deleteFiles", { context });
        break;
    }

    return { name, description };
  }, [permission, selected]);

  const handleOnClick = () => {
    if (disabled) return;
    onClick();
  };

  return (
    <StyledButton onClick={handleOnClick} selected={selected} disabled={disabled}>
      <Grid container spacing={1} wrap="nowrap">
        <Grid item xs={"auto"} alignItems="center">
          <PermissionIcon permission={permission} fontSize="small" color={!selected ? "disabled" : undefined} />
        </Grid>
        <Grid item alignItems="center">
          <Stack>
            <Typography variant="body1" fontWeight={500}>
              {name}
            </Typography>
            <Typography variant="body1" fontWeight={400} mt={0.5} color="text.secondary">
              {description}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={"auto"} alignItems="center" ml={"auto"} display={"flex"}>
          {selected ? <DoneOutlinedIcon fontSize="small" /> : <LockOutlinedIcon fontSize="small" color="disabled" />}
        </Grid>
      </Grid>
    </StyledButton>
  );
};

const StyledButton = styled(ButtonBase, { shouldForwardProp: (prop) => prop !== "selected" })<
  Pick<PermissionCardProps, "selected">
>(({ theme, selected }) => ({
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  width: "100%",
  textAlign: "left",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  borderRadius: 8,
  backgroundColor: "transparent",
  border: "1px solid",
  borderColor: theme.palette.backgroundAlpha.border,
  transition: theme.transitions.create(["background", "border"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  ...(!selected && {
    "&:hover": {
      backgroundColor: theme.palette.backgroundAlpha.hover,
    },
  }),
  ...(selected && {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.alpha,
    color: theme.palette.primary.text,
  }),
}));

export default PermissionCard;
